import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, CheckCircle } from "lucide-react";
import { CanonicalLink as Link } from "@/components/CanonicalLink";
import { submitContactRequest, contactRequestErrorMessage, reportContactConversion } from "@/lib/contact-request";

interface QuoteModalProps {
  open: boolean;
  onClose: () => void;
}

export function QuoteModal({ open, onClose }: QuoteModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  function handleClose() {
    if (loading) return;
    onClose();
    if (submitted) {
      setSubmitted(false);
      setLoading(false);
      setTermsAccepted(false);
      setTermsError(false);
      setName("");
      setPhone("");
      setEmail("");
      setMessage("");
      setSubmitError("");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    if (!termsAccepted) {
      setSubmitError("Veuillez accepter les termes et conditions avant d’envoyer votre demande.");
      return;
    }
    setSubmitError("");
    setLoading(true);
    try {
      await submitContactRequest({ name, phone, email, message, source: "popup", terms: termsAccepted });
      setSubmitted(true);
      reportContactConversion();
    } catch (error) {
      setSubmitError(contactRequestErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="quote-description">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary">Obtenir une soumission</DialogTitle>
          <DialogDescription id="quote-description" className={submitted ? "sr-only" : "text-muted-foreground text-sm"}>
            {submitted
              ? "Votre demande a été envoyée. Nous vous contacterons dans les plus brefs délais."
              : "Remplissez ce court formulaire et nous vous rappellerons rapidement. Pour une urgence, appelez-nous directement."}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-primary mb-2">Demande reçue !</h3>
            <p className="text-muted-foreground mb-6">Nous vous contacterons dans les plus brefs délais.</p>
            <Button onClick={handleClose} className="bg-primary hover:bg-primary/90 font-bold px-8">Fermer</Button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2" aria-busy={loading}>
              <div className="space-y-1.5">
                <label htmlFor="quote-name" className="text-sm font-semibold text-primary">Nom complet</label>
                <Input id="quote-name" autoComplete="name" data-testid="input-quote-name" placeholder="Jean Tremblay" required className="h-11 bg-gray-50" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="quote-phone" className="text-sm font-semibold text-primary">Numéro de téléphone</label>
                <Input id="quote-phone" autoComplete="tel" data-testid="input-quote-phone" type="tel" placeholder="(514) 555-0123" required className="h-11 bg-gray-50" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="quote-email" className="text-sm font-semibold text-primary">Courriel</label>
                <Input id="quote-email" autoComplete="email" type="email" placeholder="jean@exemple.com" required className="h-11 bg-gray-50" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="quote-message" className="text-sm font-semibold text-primary">Description du problème ou du projet</label>
                <Textarea
                  id="quote-message"
                  data-testid="input-quote-message"
                  placeholder="Ex: fuite sous l'évier de cuisine, installation de chauffe-eau..."
                  className="min-h-[90px] resize-none bg-gray-50"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                />
              </div>


              <div className="space-y-1.5">
                <label className="flex items-start gap-3 cursor-pointer text-foreground/80">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer shrink-0"
                  />
                  <span className="text-sm leading-relaxed">
                    J'accepte les{" "}
                    <a href={`${import.meta.env.BASE_URL}termes-et-conditions/`} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline">termes et conditions</a>.
                  </span>
                </label>
              </div>

              {submitError && <p role="alert" data-testid="quote-submit-error" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{submitError}</p>}

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button
                  data-testid="button-quote-submit"
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary/90 h-12 font-bold"
                >
                  {loading ? "Envoi en cours..." : submitError ? "Réessayer l’envoi" : "Envoyer ma demande"}
                </Button>
                <a
                  data-testid="button-quote-call"
                  href="tel:4389081143"
                  className="flex-1 inline-flex items-center justify-center min-h-[48px] py-3 px-4 rounded-md border-2 border-accent text-accent hover:bg-accent hover:text-white font-bold transition-colors text-sm"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Appeler directement
                </a>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
